import { useEffect, useState } from "react"
import { useWallet } from "@solana/wallet-adapter-react"
import collection from "./data/collection.json"
import { byRank } from "./lib"

export const RPC_HOST = process.env.REACT_APP_SOLANA_RPC_HOST ?? ""
// Candy machine v2 creator PDA (seeds ["candy_machine", Ck23wP…EVpz]); CMv2 verifies it on every mint,
// so it proves an NFT came from our candy machine. The collection in data/metadata.json was never created on mainnet.
const CANDY_MACHINE_CREATOR = "ESwjyEr9bQ7ny1E6LhzEHCbnQd6TpjZBNfRiTweAsBCw"

export type Ape = {
	id: string
	number: number
	name: string
	image: string
	attributes: { trait_type: string, value: string }[]
}

type DasAsset = {
	id: string
	content: {
		metadata: { name: string, attributes?: Ape["attributes"] }
		links?: { image?: string }
	}
}

const PAGE = 1000

// Helius DAS searchAssets over our apes (verified CMv2 creator), all pages
const searchApes = async (filter: object): Promise<Ape[]> => {
	if (!RPC_HOST) throw new Error("REACT_APP_SOLANA_RPC_HOST is not set")
	const items: DasAsset[] = []
	for (let page = 1; ; page++) {
		const res = await fetch(RPC_HOST, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				jsonrpc: "2.0",
				id: "apes",
				method: "searchAssets",
				params: { ...filter, creatorAddress: CANDY_MACHINE_CREATOR, creatorVerified: true, page, limit: PAGE },
			}),
		})
		const body = await res.json()
		if (!res.ok || body.error) throw new Error(body.error?.message ?? `RPC error ${res.status}`)
		items.push(...body.result.items)
		if (body.result.items.length < PAGE) break
	}
	return items
		.map(a => ({
			id: a.id,
			name: a.content.metadata.name,
			number: Number(a.content.metadata.name.match(/#(\d+)$/)?.[1]),
			image: a.content.links?.image ?? "",
			attributes: a.content.metadata.attributes ?? [],
		}))
		// Rarest (rank 1) first
		.sort(byRank(collection.rank))
}

export const useOwnedApes = () => {
	const { publicKey } = useWallet()
	const [state, setState] = useState<{ apes?: Ape[], error?: string, loading: boolean }>({ loading: false })

	useEffect(() => {
		if (!publicKey) return setState({ loading: false })
		let cancelled = false
		setState({ loading: true })
		searchApes({ ownerAddress: publicKey.toBase58() })
			.then(apes => !cancelled && setState({ apes, loading: false }))
			.catch((e: Error) => !cancelled && setState({ error: e.message, loading: false }))
		return () => { cancelled = true }
	}, [publicKey])

	return state
}

// Every ape minted so far ("discovered"), ape number → mint address; undefined while loading or if the RPC fails
let discovered: Promise<Map<number, string>> | undefined
export const useDiscovered = () => {
	const [set, setSet] = useState<Map<number, string>>()
	useEffect(() => {
		(discovered ??= searchApes({}).then(apes => new Map(apes.map(a => [a.number, a.id]))))
			.then(setSet)
			.catch(() => setSet(undefined))
	}, [])
	return set
}
